import React from "react";
import { FC, useEffect, useRef, useState } from "react";
import { LeatherIndicators, Step } from "../../../../types/wizard";
import { useSnapshot } from "valtio";
import { saveEmblemData, validateWizardSteps, wizardStore } from "../../../../stores/wizard";
import './../../QuickView.css';

interface LeatherIndicationManagerProps {
  step: Step;
}

const LeatherIndicationManager: FC<LeatherIndicationManagerProps> = ({ step }) => {
  const wizSnap = useSnapshot(wizardStore);
  const emblemData = wizSnap?.emblemData || {};
  const [indicators, setIndicators] = useState<LeatherIndicators[]>([]);
  const dropdownRefs = useRef<Array<HTMLDivElement | null>>([]);
  const stepProductOpt = wizSnap?.designRequest.productOptions?.emblemLeather;
  const emblemProductOption = stepProductOpt?.setEmblemLeatherOption;
  const isInvalid = wizSnap.errors[step?.id]?.length;

  useEffect(() => {
    const initialIndicators = emblemData[step.id]?.indicators || [];
    setIndicators(initialIndicators);
  }, [step.id, emblemData]);

  const saveIndicators = (updatedIndicators: LeatherIndicators[]) => {
    setIndicators(updatedIndicators);
    saveEmblemData(step.id, { indicators: updatedIndicators });
    validateWizardSteps(step.id);
  };

  const addInput = () => {
    const updatedIndicators = [
      ...indicators,
      { id: indicators.length, x: null, y: null, indicator: '', instructions: '' },
    ];
    saveIndicators(updatedIndicators);
  };

  const removeInput = (index: number) => {
    const updatedIndicators = indicators.filter((_, i) => i !== index);
    saveIndicators(updatedIndicators);
  };

  const handleColoringChange = (index: number, coloring: string) => {
    const updatedIndicators = indicators.map((item, i) =>
      i === index ? { ...item, indicator: coloring } : item
    );
    saveIndicators(updatedIndicators);
  };

  const handleSpecialInstructions = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
    index: number
  ) => {
    const instructions = event.target.value;
    const updatedIndicators = indicators.map((item, i) =>
      i === index ? { ...item, instructions } : item
    );
    saveIndicators(updatedIndicators);
  };

  return (<>
    {emblemProductOption === "AllowWEIToChoose" ? null : (
      <>
        <div className="relative rounded bg-white">
          <div className="flex flex-col justify-left">
            {indicators.map((i, index) => (
              <React.Fragment key={index}>
                <div className="flex flex-col space-y-2">
                  <div
                    ref={(el) => (dropdownRefs.current[index] = el)}
                    className="w-full mt-1"
                  >
                    <label className="block uppercase font-bold mb-2.5">{`Leather Coloring Indication ${index + 1}`}</label>

                    <div className="gap-4 radio-button mt-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name={`coloring-${index}`}
                          value="Dark"
                          checked={i.indicator === 'Dark'}
                          onChange={() => handleColoringChange(index, 'Dark')}
                        />
                        Dark
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name={`coloring-${index}`}
                          value="Light"
                          checked={i.indicator === 'Light'}
                          onChange={() => handleColoringChange(index, 'Light')}
                        />
                        Light
                      </label>
                    </div>
                  </div>

                  <div className="w-full flex flex-col items-start gap-2 mt-2 mb-4">
                    <h2 className="">
                      <span className="font-bold uppercase mr-2">Special Instructions</span>
                      <span>(Required)</span>
                    </h2>
                    <textarea
                      className="border w-full rounded h-24 p-1 outline-none"
                      style={{ border: '1px solid #ccc' }}
                      onChange={(e) => handleSpecialInstructions(e, index)}
                      placeholder={
                        step?.specialInstructionsPlaceholder ||
                        'Please provide additional, detailed instructions here to help with the production of your design.'
                      }
                      value={i.instructions}
                    />
                  </div>

                  <div className="flex flex-row justify-end">
                    <button
                      className="outline-none hover:outline-none border-0 underline text-sm text-error px-0 py-1 bg-inherit"
                      onClick={() => removeInput(index)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </React.Fragment>
            ))}

            <div className="flex flex-col justify-left py-4">
              <div className="flex flex-row">
                <button
                  className="btn-file bg-black text-white underline rounded"
                  onClick={addInput}
                >
                  Add Leather Coloring Indication
                </button>
              </div>
            </div>
          </div>
        </div>
        {isInvalid &&
          <div className="text-[#a61613] text-sm leading-4 mt-1">
            <span>{wizSnap.errors[step?.id][0]}</span>
          </div>
        }
      </>
    )}
  </>);
};

export default LeatherIndicationManager;